// Human-readable URL state for list pages (page, sort, search, filters).
//
// Values that match the page's default are left out of the URL entirely, so the
// default view is a bare path and a shared link only carries what the user
// actually changed:
//
//   /individuals?page=2&sort=-createdAt&search=ahmed&gender=female&from=2026-01-01
//
// Sort uses the usual leading-minus convention: "-createdAt" is descending,
// "createdAt" ascending.

export type SortState = Array<{ id: string; desc: boolean }>;

export type DateRange = {
  fromDate: Date | string | null;
  toDate: Date | string | null;
};

export const PAGE_PARAM = "page";
export const PAGE_SIZE_PARAM = "pageSize";
export const SORT_PARAM = "sort";
export const SEARCH_PARAM = "search";
export const FROM_PARAM = "from";
export const TO_PARAM = "to";

export const DEFAULT_PAGE_SIZE = 20;

/** Keys this helper manages directly; everything else in the URL is left alone. */
const RESERVED_PARAMS = [
  PAGE_PARAM,
  PAGE_SIZE_PARAM,
  SORT_PARAM,
  SEARCH_PARAM,
  FROM_PARAM,
  TO_PARAM,
];

const isDateRange = (value: any): boolean =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("fromDate" in value || "toDate" in value);

/** Dates travel as YYYY-MM-DD, matching the en-CA format the API filters use. */
const formatDateParam = (value: Date | string | null): string => {
  if (!value) return "";
  if (value instanceof Date)
    return isNaN(value.getTime()) ? "" : value.toLocaleDateString("en-CA");
  return String(value).slice(0, 10);
};

/**
 * Parse YYYY-MM-DD in local time. `new Date("2026-01-01")` is treated as UTC and
 * can land on the previous day west of Greenwich, which would shift the filter.
 */
const parseDateParam = (value: string | null): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    const loose = new Date(value);
    return isNaN(loose.getTime()) ? null : loose;
  }
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const serializeSort = (sorted: SortState | undefined): string =>
  (sorted ?? [])
    .filter((entry) => entry?.id)
    .map((entry) => (entry.desc ? `-${entry.id}` : entry.id))
    .join(",");

export const parseSort = (
  value: string | null,
  defaultSort: SortState
): SortState => {
  if (value === null) return defaultSort;
  const parsed = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) =>
      part.startsWith("-")
        ? { id: part.slice(1), desc: true }
        : { id: part, desc: false }
    );
  return parsed.length > 0 ? parsed : defaultSort;
};

export const parsePage = (searchParams: URLSearchParams): number => {
  const page = Number(searchParams.get(PAGE_PARAM));
  return Number.isFinite(page) && page > 0 ? page : 1;
};

export const parsePageSize = (
  searchParams: URLSearchParams,
  defaultPageSize: number = DEFAULT_PAGE_SIZE
): number => {
  const pageSize = Number(searchParams.get(PAGE_SIZE_PARAM));
  return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : defaultPageSize;
};

export const parseSearch = (searchParams: URLSearchParams): string =>
  searchParams.get(SEARCH_PARAM) ?? "";

/**
 * Rebuild a filter object from flat query params, using `defaultFilterValue` to
 * know which keys exist and what to fall back to.
 *
 * Absent and present-but-empty are deliberately different: a key whose default
 * is non-empty (Payments' `status: "active"`) has to be clearable, so `?status=`
 * means "explicitly none" while a missing `status` means "use the default".
 */
export const parseFilter = <T extends Record<string, any>>(
  searchParams: URLSearchParams,
  defaultFilterValue: T
): T => {
  const result: Record<string, any> = { ...defaultFilterValue };

  Object.entries(defaultFilterValue ?? {}).forEach(([key, defaultVal]) => {
    if (isDateRange(defaultVal)) {
      result[key] = {
        ...(defaultVal as DateRange),
        fromDate: searchParams.has(FROM_PARAM)
          ? parseDateParam(searchParams.get(FROM_PARAM))
          : (defaultVal as DateRange).fromDate ?? null,
        toDate: searchParams.has(TO_PARAM)
          ? parseDateParam(searchParams.get(TO_PARAM))
          : (defaultVal as DateRange).toDate ?? null,
      };
      return;
    }
    if (searchParams.has(key)) result[key] = searchParams.get(key) ?? "";
  });

  return result as T;
};

/**
 * Build the query string for the current list state, preserving any params this
 * helper does not own (tab, ids, and anything else already on the URL).
 */
export const buildListParams = (
  current: URLSearchParams,
  {
    page,
    pageSize,
    sorted,
    searchValue,
    filterValue,
    defaultFilterValue,
    defaultSort,
    defaultPageSize = DEFAULT_PAGE_SIZE,
  }: {
    page: number;
    pageSize?: number;
    sorted?: SortState;
    searchValue?: string;
    filterValue?: Record<string, any>;
    defaultFilterValue?: Record<string, any>;
    defaultSort?: SortState;
    defaultPageSize?: number;
  }
): URLSearchParams => {
  const params = new URLSearchParams(current);

  // Drop everything we own first so a cleared filter cannot leave a stale value
  // behind, then re-add only what differs from the defaults.
  RESERVED_PARAMS.forEach((key) => params.delete(key));
  Object.keys(defaultFilterValue ?? filterValue ?? {}).forEach((key) =>
    params.delete(key)
  );

  if (page > 1) params.set(PAGE_PARAM, String(page));
  if (pageSize && pageSize !== defaultPageSize)
    params.set(PAGE_SIZE_PARAM, String(pageSize));

  const sortValue = serializeSort(sorted);
  if (sortValue && sortValue !== serializeSort(defaultSort))
    params.set(SORT_PARAM, sortValue);

  if (searchValue) params.set(SEARCH_PARAM, searchValue);

  Object.entries(filterValue ?? {}).forEach(([key, value]) => {
    const defaultVal = defaultFilterValue?.[key];

    if (isDateRange(value) || isDateRange(defaultVal)) {
      const from = formatDateParam((value as DateRange)?.fromDate ?? null);
      const to = formatDateParam((value as DateRange)?.toDate ?? null);
      if (from) params.set(FROM_PARAM, from);
      if (to) params.set(TO_PARAM, to);
      return;
    }

    if (value === null || value === undefined) return;
    if (defaultFilterValue && key in defaultFilterValue && value === defaultVal)
      return;
    if (!defaultFilterValue && value === "") return;

    params.set(key, String(value));
  });

  return params;
};
